import { useCallback, useState } from 'react';
import Game from '../Game';
import Board from '../lib/board';
import { GameMode, SavableGameMode, ScoreData } from '../lib/types';
import { formatWithCommas, updateAverage } from '../lib/util';
import { getGameStorage, setGameStorage } from '../localStorage/game';
import { getScoreStorage, setScoreStorage } from '../localStorage/score';
import {
  MenuButton,
  MenuContainer,
  MenuScore,
  MenuTitle,
  ModeButtons,
  ModeButtonsInner,
  ModeInfo,
  ModeInfoButton,
  ModeInfoInner,
  ModeInfoTitle,
} from './mainMenu.styles';

export default function MainMenu() {
  const [modeInfo, setModeInfo] = useState<SavableGameMode | null>(null);

  const [board, setBoard] = useState<Board | null>(() => {
    const gameState = getGameStorage();
    if (gameState.mode !== null) {
      try {
        return new Board({
          ...gameState,
          onUpdate: setGameStorage
        });
      } catch (err) {
        console.error(err);
        return null;
      }
    }
    return null;
  });

  const [scores, setScores] = useState<ScoreData>(getScoreStorage);

  const updateScore = useCallback((mode: SavableGameMode, score: number) => {
    setScores((scores) => {
      const { best, average, count } = scores[mode];
      const newBest = Math.max(best, score);
      const newAverage = Math.round(updateAverage(score, average, count));
      const newCount = count + 1;
      const newScores = {
        ...scores,
        [mode]: {
          best: newBest,
          average: newAverage,
          count: newCount,
        },
      };
      setScoreStorage(newScores);
      return newScores;
    });
  }, []);

  const startGame = useCallback((mode: GameMode) => {
    const newBoard = new Board({
      mode,
      onUpdate: setGameStorage
    });
    setBoard(newBoard);
    if (mode !== GameMode.Tutorial) {
      setGameStorage(newBoard.serialize());
    }
  }, []);

  const stopGame = useCallback(() => {
    setBoard(null);
    setGameStorage(null);
  }, []);

  const restartGame = useCallback(() => {
    const mode = board?.mode ?? GameMode.Classic;
    stopGame();
    setBoard(null);
    startGame(mode);
  }, [board?.mode, startGame, stopGame]);

  if (board !== null) {
    return <Game board={board}
                 scores={scores}
                 restart={restartGame}
                 exit={stopGame}
                 updateScore={updateScore} />;
  }

  const renderModeInfo = (mode: SavableGameMode) => {
    switch (mode) {
      case GameMode.Classic:
        return (
          <p>Number and solid discs are dropped. The game starts with 30 drops per level.</p>
        );
      case GameMode.Blitz:
        return (
          <p>Only number discs are dropped. You only get 5 drops per level!</p>
        );
      case GameMode.Sequence:
        return (
          <p>The same disc order every time you play! Both number and solid discs drop.</p>
        );
      default:
        return null;
    }
  };

  const renderModeButton = (mode: GameMode) => (
    <ModeButtons>
      <ModeButtonsInner>
        <MenuButton outline={mode === GameMode.Tutorial}
                    onClick={() => startGame(mode)}>
          {mode === GameMode.Tutorial ? 'How to Play' : GameMode[mode]}
        </MenuButton>
        {mode !== GameMode.Tutorial && (
          <MenuScore>
            {scores[mode].best === 0
              ? 'No High Score'
              : `High Score: ${formatWithCommas(scores[mode].best)}`}
          </MenuScore>
        )}
      </ModeButtonsInner>
      <ModeInfoButton disabled={mode === GameMode.Tutorial}
                      onClick={mode === GameMode.Tutorial
                        ? undefined
                        : () => setModeInfo(mode)}
      >?</ModeInfoButton>
    </ModeButtons>
  );

  return <MenuContainer>
    {modeInfo !== null && (
      <ModeInfo>
        <ModeInfoInner>
          <ModeInfoTitle>{GameMode[modeInfo]} Mode</ModeInfoTitle>
          {renderModeInfo(modeInfo)}
          <MenuButton onClick={() => setModeInfo(null)}>Got it!</MenuButton>
        </ModeInfoInner>
      </ModeInfo>
    )}
    <MenuTitle>Drop7</MenuTitle>
    {renderModeButton(GameMode.Classic)}
    {renderModeButton(GameMode.Blitz)}
    {renderModeButton(GameMode.Sequence)}
    {renderModeButton(GameMode.Tutorial)}
  </MenuContainer>;
}
